const { WEBSOCKET_URL } = JSON.parse(document.getElementById('JS_BOOTSTRAP').textContent);
import { v4 as uuid } from 'uuid';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Ensure links can specify target/rel (needed for opening in a new tab)
// We don't remove any existing defaults; we only add to the allowlist.
const SANITIZE_OPTIONS = { ADD_ATTR: ['target', 'rel'] };

function renderSafeHtmlFromMarkdown(mdOrHtml) {
  // marked will pass-through raw HTML; DOMPurify will sanitize it while preserving target/rel
  const html = marked.parse(mdOrHtml);
  return DOMPurify.sanitize(html, SANITIZE_OPTIONS);
}

function safeParse(value) {
  try {
    if (typeof value === 'string') return JSON.parse(value);
    return value;
  } catch (e) {
    return value;
  }
}

function triggerCsvDownload(filename, csvText, mimetype = 'text/csv') {
  try {
    const name = filename && String(filename).trim() ? filename : 'export.csv';
    const href = `data:${mimetype};charset=utf-8,${encodeURIComponent(csvText)}`;
    const a = document.createElement('a');
    a.setAttribute('href', href);
    a.setAttribute('download', name);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    console.warn('Failed to trigger CSV download', e);
  }
}

export async function createSocket(store, socketPath = '/ws/agent-chat/') {
  // This is because our env vars are inconsistent depending on the deploy environment
  const cleanedWebsocketUrl = WEBSOCKET_URL.endsWith('/') ? WEBSOCKET_URL.slice(0, -1) : WEBSOCKET_URL;
  console.log('cleanedWebsocketUrl', cleanedWebsocketUrl);
  const normalizedPath = socketPath.startsWith('/') ? socketPath : `/${socketPath}`;
  const socket = new WebSocket(`${cleanedWebsocketUrl}${normalizedPath}`);
  return new Promise((resolve, reject) => {
    try {
      console.log('AT BEGINNING OF PROMISE');
      setupEvents(socket, store);
      console.log('AFTER SETUP EVENTS');
      socket.onopen = () => {
        console.log('ON OPEN');
        const conversationId = uuid(); // if you're worried about collision, you should be building a bunker, since comet hitting earth is more likely- source: meme
        store.conversations[conversationId] = {
          id: conversationId,
          messages: [],
          system_prompt: '',
          context: {},
          streamingMessage: {
            text: '',
            role: 'assistant',
          },
        };
        console.log('AFTER ON OPEN');
        store.currentConversationId = conversationId;
        console.log('RESOLVING AT END OF CREATE SOCKET');
        resolve(socket);
      };
      socket.onerror = error => {
        reject(error);
      };
    } catch (error) {
      reject(error);
    }
  });
}

export async function onConnectedMessageEvent(data, store) {
  console.log('connected');
  const md = [
    'Ask me anything! Here\'s what I can access:',
    '',
    '- Client dossiers',
    '- Meetings',
    '- Portfolios',
    '- Documents',
    '- Risk profiles',
    '- Values set',
    '- Recommendations',
    '- Calculator',
    '- Web search',
    '- Email (only available if you authorize Outlook, Gmail coming soon!)',
    '',
    'Responses may be instant or take up to 10 minutes to complete, depending on how deep I have to search.',
  ].join('\n');
  const formattedText = renderSafeHtmlFromMarkdown(md);
  await store.pushMessage({ text: formattedText, role: 'assistant' });
}

export async function onEndMessageEvent(data, store) {
  console.log('END MESSAGE', data);
  const formattedText = renderSafeHtmlFromMarkdown(data.message.text);
  store.pushMessage({ ...data.message, text: formattedText });
  store.setStreamingMessage({ text: '', role: 'assistant' });
  try {
    store.resetToolActivity();
  } catch (e) {}
}
export async function onMessageTokenEvent(data, store) {
  console.log('TOKEN MESSAGE', data);
  store.pushToStreamingMessage(data.message);
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    try {
      return String(value);
    } catch (e2) {
      return '[unserializable]';
    }
  }
}

export async function onToolCallEvent(data, store) {
  try {
    const name = data.name || 'tool';
    const args = data.arguments != null ? safeStringify(data.arguments) : '';
    const md = `Calling ${name}${args ? `\n\n\u0060\u0060\u0060json\n${args}\n\u0060\u0060\u0060` : ''}`;
    const formatted = renderSafeHtmlFromMarkdown(md);
    await store.pushMessage({ text: formatted, role: 'assistant' });
  } catch (e) {
    console.warn('Failed to render tool_call event', e);
  }
}

export async function onToolEvent(data, store) {
  try {
    const description = data.message.description || 'Calling tool';
    // const formatted = DOMPurify.sanitize(marked.parse(`${String(description)}...`));
    // await store.pushMessage({ text: formatted, role: 'assistant' });
    // start spinner until tool finishes
    try {
      store.startToolActivity(description);
    } catch (e) {}
  } catch (e) {
    console.warn('Failed to render tool_call event', e);
  }
}


export async function onToolOutputEvent(data, store) {
  try {
    const name = data.name || 'tool';
    // below if is scaffold for csv
    // const output = safeParse(data.output);
    // if (output && typeof output === 'object' && output.type === 'csv' && output.data) {
    //   triggerCsvDownload(output.filename, String(output.data), output.mimetype || 'text/csv');
    //   const md = `Prepared CSV download: ${output.filename || 'export.csv'}`;
    //   const formatted = renderSafeHtmlFromMarkdown(md);
    //   await store.pushMessage({ text: formatted, role: 'assistant' });
    // } else {
    const formatted = renderSafeHtmlFromMarkdown(String(name));
    await store.pushMessage({ text: formatted, role: 'assistant' });
    // stop spinner when tool outputs
    // }
    try {
      store.finishToolActivity();
    } catch (e) {}
  } catch (e) {
    console.warn('Failed to render tool_output event', e);
  }
}

export async function onToolErrorEvent(data, store) {
  try {
    const name = data.name || 'tool';
    const formatted = renderSafeHtmlFromMarkdown(String(name));
    await store.pushMessage({ text: formatted, role: 'assistant' });
    // stop spinner on error as well
    try {
      store.finishToolActivity();
    } catch (e) {}
  } catch (e) {
    console.warn('Failed to render tool_error event', e);
  }
}

export async function onAgentUpdatedEvent(data, store) {
}

export const socketEvents = {
  'connected': onConnectedMessageEvent,
  'endMessage': onEndMessageEvent,
  'messageToken': onMessageTokenEvent,
  'agent_updated': onAgentUpdatedEvent,
  'tool': onToolEvent,
  // 'tool_output': onToolOutputEvent,
  // 'tool_error': onToolErrorEvent,
  // 'tool_call': onToolCallEvent,
};

export async function setupEvents(socket, store) {
  socket.onmessage = e => {
    const data = JSON.parse(e.data);
    const event = socketEvents[data.type];
    if (event) {
      event(data, store);
    } else {
      console.log('Unhandled WS event', data);
    }
  };
  socket.onclose = e => {
    console.error('Chat socket closed unexpectedly, trying again in 1 second.');
    setTimeout(async () => {
      // store.socket = await createSocket(store);
    }, 1000);
  };
}
